import { Box, Stack, Typography } from "@mui/material";
import { LineChart } from "@mui/x-charts";
import CalendarTodayIcon from "assets/svg/CalendarTodayIcon";
import { AxiosResponse } from "axios";
import { commonStyle } from "pages/Dashboard/common.styles";
import { useEffect, useRef, useState } from "react";
import {
  Area,
  AreaChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { MonthlyVolumeResponse } from "reduxStore/dataTypes";
import { fetchAdminMonthlyTransactionVolume } from "services/apiService";
import theme, { responsiveFontSize } from "themes/Theme";
import { formatNumber, graphFormatter } from "utils/formatter";

const EarningGraph = () => {
  const [monthlyData, setMonthlyData] = useState<any[] | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const containerRef = useRef<HTMLDivElement>(null);
  
  const getYearGraphData = async () => {
    try {
      setLoading(true);
      const response: AxiosResponse<MonthlyVolumeResponse> =
        await fetchAdminMonthlyTransactionVolume();
      if (response && response.status === 200) {
        const converted = Object.entries(response?.data?.data?.monthlyData).map(
          ([month, values]) => ({
            name: month,
            totalTransfer: Number(values.totalTransfer),
            totalReceive: Number(values.totalReceive),
          })
        );
        setMonthlyData(converted);
      }
    } catch (error) {
      setMonthlyData(null);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    getYearGraphData();
  }, []);

// used hasData to check if data is available
  const hasData = monthlyData && monthlyData.length > 0;

  return (
    <Box ref={containerRef} sx={styles.container}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
        }}
      >
        <Typography
          sx={{
            fontWeight: 400,
            fontSize: responsiveFontSize(18),
            color: theme.palette.text.primary,
          }}
        >
          Transaction Overview
        </Typography>
        <Stack
          direction={"row"}
          alignItems={"center"}
          sx={styles.calendarBg}
          gap={1}
        >
          <CalendarTodayIcon />
          <Typography sx={styles.calendarText}>This Year</Typography>
        </Stack>
      </Box>
      
      {loading ? (
        <Box sx={styles.noDataContainer}>
          <Typography sx={styles.noDataText}>Loading...</Typography>
        </Box>
      ) : !hasData ? (
        <Box sx={styles.noDataContainer}>
          <Typography sx={styles.noDataText}>No transaction Overview available</Typography>
        </Box>
      ) : (
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart
            width={
              containerRef?.current?.getBoundingClientRect().width
                ? containerRef?.current?.getBoundingClientRect().width - 40
                : 400
            }
            height={300}
            data={monthlyData || []}
            margin={{
              top: 10,
              right: 30,
              left: 0,
              bottom: 0,
            }}
          >
            <XAxis dataKey="name" />
            <YAxis tickFormatter={graphFormatter} />
            <Tooltip
              formatter={(value: number, name: string) => {
                return [
                  `${value}`,
                  name === "totalTransfer" ? "Total Transfer" : "Total Receive",
                ];
              }}
            />
            <Area
              type="monotone"
              dataKey="totalTransfer"
              stackId="1"
              stroke={theme.palette.primary.main}
              fill={theme.palette.primary.main}
            />
            <Area
              type="monotone"
              dataKey="totalReceive"
              stackId="1"
              stroke={theme.palette.primary.light}
              fill={theme.palette.primary.light}
            />
          </AreaChart>
        </ResponsiveContainer>
      )}
    </Box>
  );
};
export default EarningGraph;
const styles = {
  calendarBg: {
    backgroundColor: "rgba(245, 245, 246, 1)",
    borderRadius: 3,
    boxShadow: `rgba(8, 60, 61, 0.27) 0px 1px 5px 0px, rgba(8, 60, 61, 0.06) 0px 1px 5px 0px`,
    padding: "5px 8px",
    width: "fit-content",
  },

  container: {
    borderRadius: 3,
    boxShadow: `rgba(8, 60, 61, 0.27) 0px 1px 5px 0px, rgba(8, 60, 61, 0.06) 0px 1px 5px 0px`,
    backgroundColor: "rgb(255, 255, 255)",
    p: 3,
    width: "100%",
  },
  calendarText: {
    fontSize: responsiveFontSize(12),
    fontWeight: 400,
    color: theme.palette.text.primary,
  },
  earningUnit: {
    fontSize: responsiveFontSize(14),
    fontWeight: 400,
    color: theme.palette.primary.main,
  },
  earningValue: {
    fontSize: responsiveFontSize(24),
    fontWeight: 400,
    color: theme.palette.text.primary,
    marginLeft: 1,
  },
  totalEarningText: {
    fontSize: responsiveFontSize(14),
    fontWeight: 400,
    color: theme.palette.text.secondary,
  },
  noDataContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: 300,
    width: "100%",
  },
  noDataText: {
    fontSize: responsiveFontSize(16),
    fontWeight: 400,
    color: theme.palette.text.secondary,
    textAlign: "center",
  },
};

const chartData1 = [
  { month: "Sept", value: 50 },
  { month: "Oct", value: 60 },
  { month: "Nov", value: 65 },
  { month: "Dec", value: 55 },
  { month: "Jan", value: 45 },
];
const chartData2 = [
  { month: "Sept", value: 60 },
  { month: "Oct", value: 84 },
  { month: "Nov", value: 70 },
  { month: "Dec", value: 50 },
  { month: "Jan", value: 35 },
];
